import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Axios from "axios";
import Cookies from "js-cookie";
import { Box, Container, Paper, CircularProgress } from "@mui/material";
import { getBaseNonEvRevenueAPI, getOfficesWithPersonaAPI, postOperationImprovementStatsAPI } from "@/util/api/revenueSimulations";
import { getEvExternalChargingRatiosAPI, getCarTypeMonthlyStatsAPI, getEvCarMonthlyStatsAPI } from "@/util/api/monthlyCarStats";
import { OperationImprovementStats, OfficeWithPersona, VoiceItem } from "@/models/goFurther";
import { EvExternalChargingRatios, CarTypeMonthlyStats, EvCarMonthlyStats } from "@/models/monthlyCarStats";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { GoFurtherAnalytics } from "@/util/analytics";
import { useSetting } from "@/context";
import { GoFurtherConstants } from "./GoFurtherConstants";
import { BACKGROUND_COLOR, TEXT_COLOR, GO_FURTHER_PAGE_SX } from "./GoFurtherStyles";
import { getJSTCurrentTime } from "@/util/datetime";
import {
  convertStreetViewUrl,
  buildGoogleMapUrl,
  buildGoChargeUrl,
  processComparisonData,
  computePreviousMonthKey,
  type ComparisonDataItem,
  type VehicleChartDataItem,
} from "./GoFurtherUtils";
import toast from "@/components/hoc/SnackbarAlert";
import { PreparingLayout } from "./components/GoFurtherPrimitives";
import GoFurtherHeroSection from "./components/GoFurtherHeroSection";
import GoFurtherOfficeSimulationSection from "./components/GoFurtherOfficeSimulationSection";
import GoFurtherSuccessCaseSection from "./components/GoFurtherSuccessCaseSection";
import GoFurtherPhaseSection from "./components/GoFurtherPhaseSection";
import GoFurtherActionSection from "./components/GoFurtherActionSection";
import GoFurtherClosingSection from "./components/GoFurtherClosingSection";

const YEN_PER_MAN_EN = 10000;
const MONTH_CHANGE_DEBOUNCE_MS = 500;
const MONTHLY_STATS_INITIAL_FETCH_DELAY_MS = 500;

const toYYYYMM = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return value.replace(/-/g, "");
};

const GoFurther: React.FC = () => {
  const { updateTitleHeader, updateRouteTitleHeader, clearRouteTitleHeader } = useSetting();
  const [loading, setLoading] = useState<boolean>(true);
  const [offices, setOffices] = useState<OfficeWithPersona[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<OfficeWithPersona | null>(null);
  const [stats, setStats] = useState<OperationImprovementStats | null>(null);
  const [baseNonEvRevenue, setBaseNonEvRevenue] = useState<number | null>(null);
  const [hasOperationImprovementStatsConflict, setHasOperationImprovementStatsConflict] = useState<boolean>(false);
  const [selectedYyyymm, setSelectedYyyymm] = useState<string>(computePreviousMonthKey);
  const fetchYyyymm = useRef<string>(selectedYyyymm);
  const [fetchRevision, setFetchRevision] = useState(0);
  const prevSuccessYyyymm = useRef<string>(selectedYyyymm);
  const monthChangeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMonthChangeFetchRef = useRef(false);
  const monthlyStatsLastFetchedOfficeRef = useRef<number | null>(null);
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const [evExternalChargingRatios, setEvExternalChargingRatios] = useState<EvExternalChargingRatios | null>(null);
  const [carTypeMonthlyStats, setCarTypeMonthlyStats] = useState<CarTypeMonthlyStats | null>(null);
  const [evCarMonthlyStats, setEvCarMonthlyStats] = useState<EvCarMonthlyStats | null>(null);

  const isPersonaAB = selectedOffice?.personaType === "A" || (selectedOffice?.personaType?.startsWith("B") ?? false);

  const handleOperationImprovementStatsError = useCallback((error: unknown, message: string) => {
    if (Axios.isAxiosError(error) && error.response?.status === 409) {
      setHasOperationImprovementStatsConflict(true);
      setStats(null);
      return;
    }

    console.error(message, error);
  }, []);

  const onChangeOffice = useCallback((office: OfficeWithPersona | null) => {
    setSelectedOffice(office);

    // 営業所切り替え時は年月・フェッチ状態をリセットし、新営業所で前月から始める
    const defaultYyyymm = computePreviousMonthKey();
    setSelectedYyyymm(defaultYyyymm);
    fetchYyyymm.current = defaultYyyymm;
    prevSuccessYyyymm.current = defaultYyyymm;
    monthlyStatsLastFetchedOfficeRef.current = null;
    if (monthChangeDebounceRef.current) {
      clearTimeout(monthChangeDebounceRef.current);
      monthChangeDebounceRef.current = null;
    }

    // 前営業所の月次統計を保持し続けると、新営業所のデータ取得完了までの間
    // 別営業所のデータ（残像）が表示されてしまうため、切替時点で破棄する
    setEvExternalChargingRatios(null);
    setCarTypeMonthlyStats(null);
    setEvCarMonthlyStats(null);

    // 切替直前の月変更/月次統計フェッチが isMonthLoading=true のまま中断された場合に
    // 備えてリセットする。新営業所側のフェッチが stats 待ち等で遅延・未実行になっても
    // （例: 409 コンフリクトで stats が null のままになるケース）スピナーが残り続けない
    // ようにするため、実際のフェッチ開始時（doFetch）で改めて true にする。
    setIsMonthLoading(false);

    Cookies.set(SINGLE_OFFICE_ID, office?.id?.toString() || "");
    GoFurtherAnalytics.trackOfficeSelect(office?.id, office?.displayName);
  }, []);

  useEffect(() => {
    return () => {
      if (monthChangeDebounceRef.current) clearTimeout(monthChangeDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    updateTitleHeader(GoFurtherConstants.Title);
    updateRouteTitleHeader(GoFurtherConstants.Title);

    return () => {
      clearRouteTitleHeader();
    };
  }, [updateTitleHeader, updateRouteTitleHeader, clearRouteTitleHeader]);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        setLoading(true);
        const officesRes = await getOfficesWithPersonaAPI();
        const officeList = officesRes.data;
        setOffices(officeList || []);

        if (officeList && officeList.length > 0) {
          const cookieOfficeIdRaw = Cookies.get(SINGLE_OFFICE_ID);
          const cookieOfficeId = cookieOfficeIdRaw && cookieOfficeIdRaw.trim() !== "" ? Number(cookieOfficeIdRaw) : NaN;
          const matchedOffice = Number.isFinite(cookieOfficeId)
            ? officeList.find((office) => office.id === cookieOfficeId)
            : undefined;
          const office =
            matchedOffice ||
            officeList.reduce((minOffice, currentOffice) =>
              currentOffice.id < minOffice.id ? currentOffice : minOffice,
            );

          onChangeOffice(office);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to fetch GoFurther data", error);
        setLoading(false);
      }
    };

    fetchOffices();
  }, [onChangeOffice]);

  const applyBaseNonEvRevenue = useCallback(
    async (officeId: number, revenueInManEn: number) => {
      try {
        setLoading(true);
        setHasOperationImprovementStatsConflict(false);
        const revenueInYen = Math.round(revenueInManEn * YEN_PER_MAN_EN);
        const statsRes = await postOperationImprovementStatsAPI(officeId, revenueInYen);
        setStats(statsRes.data as OperationImprovementStats);
      } catch (error) {
        handleOperationImprovementStatsError(error, "Failed to fetch operation improvement stats");
      } finally {
        setLoading(false);
      }
    },
    [handleOperationImprovementStatsError],
  );

  const onApplyBaseNonEvRevenue = useCallback(
    async (revenue: number) => {
      if (!selectedOffice) return;
      setBaseNonEvRevenue(revenue);
      await applyBaseNonEvRevenue(selectedOffice.id, revenue);
    },
    [selectedOffice, applyBaseNonEvRevenue],
  );

  useEffect(() => {
    let isActive = true;

    const fetchBaseRevenueAndStats = async () => {
      if (!selectedOffice) return;

      setLoading(true);
      setHasOperationImprovementStatsConflict(false);

      try {
        const baseRevenueRes = await getBaseNonEvRevenueAPI(selectedOffice.id);
        if (!isActive) return;

        const nextBaseNonEvRevenueYen = baseRevenueRes.data.baseNonEvRevenue;
        const nextBaseNonEvRevenueManEn = nextBaseNonEvRevenueYen / YEN_PER_MAN_EN;
        setBaseNonEvRevenue(nextBaseNonEvRevenueManEn);

        // 営業所選択直後は「反映する」が押下されたものとして統計を再取得する
        const statsRes = await postOperationImprovementStatsAPI(selectedOffice.id, nextBaseNonEvRevenueYen);
        if (!isActive) return;

        setHasOperationImprovementStatsConflict(false);
        setStats(statsRes.data as OperationImprovementStats);
      } catch (error) {
        if (isActive) {
          handleOperationImprovementStatsError(error, "Failed to fetch base revenue or operation improvement stats");
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchBaseRevenueAndStats();

    return () => {
      isActive = false;
    };
  }, [handleOperationImprovementStatsError, selectedOffice]);

  useEffect(() => {
    if (!selectedOffice) {
      setEvExternalChargingRatios(null);
      setCarTypeMonthlyStats(null);
      setEvCarMonthlyStats(null);
      return;
    }

    const yyyymm = fetchYyyymm.current;
    // デバウンス経由の月変更かどうかを判定し、フラグをリセット。
    // 初回表示・営業所切替は false になる。
    const isMonthChangeFetch = isMonthChangeFetchRef.current;
    isMonthChangeFetchRef.current = false;

    // 初回表示・営業所切替: stats（プライマリ API）が取得済みになるまで待機し、
    // 同一営業所への重複フェッチを防ぐ。stats 変化のたびに effect が再実行されるため ref でガード。
    if (!isMonthChangeFetch) {
      if (!stats) return;
      if (monthlyStatsLastFetchedOfficeRef.current === selectedOffice.id) return;
      // ref はタイマー起動前ではなく doFetch 内で設定する。
      // タイマー待機中に stats が変化して effect が再実行された場合、
      // ここで ref を設定するとガードに弾かれてフェッチが行われなくなるため。
    }

    let isActive = true;
    let delayTimer: ReturnType<typeof setTimeout> | null = null;

    const doFetch = async () => {
      setIsMonthLoading(true);
      try {
        const [ratiosRes, carTypeRes, evCarRes] = await Promise.all([
          getEvExternalChargingRatiosAPI(selectedOffice.id, yyyymm),
          getCarTypeMonthlyStatsAPI(selectedOffice.id, yyyymm),
          getEvCarMonthlyStatsAPI(selectedOffice.id, yyyymm),
        ]);
        // 中断された場合、後続の effect 実行に再フェッチを委ねるため
        // ref もローディング状態もここでは更新しない（isActive が false のまま
        // 更新すると「フェッチ済み」扱いになり、以後再取得されなくなるため）
        if (!isActive) return;

        // 3 API すべてにデータがある月のみ有効とする（年月を連動させる制約のため、いずれか 1 つでも欠損している月はリジェクト）
        const hasData =
          ratiosRes.data.evCarStats.length > 0 &&
          carTypeRes.data.carTypeStats.length > 0 &&
          evCarRes.data.evCars.length > 0;

        if (!hasData) {
          toast.error("選択した年月のデータがありません。年月を元に戻します。");
          setSelectedYyyymm(prevSuccessYyyymm.current);
          fetchYyyymm.current = prevSuccessYyyymm.current;
          setIsMonthLoading(false);
          return;
        }

        // ref は成功確定後にのみ更新する。フェッチ開始時点で更新すると、
        // 中断・失敗時に「この営業所は取得済み」と誤認され、
        // stats が変化しない限り再取得されずローディング表示が固まったままになる。
        monthlyStatsLastFetchedOfficeRef.current = selectedOffice.id;
        prevSuccessYyyymm.current = yyyymm;
        setEvExternalChargingRatios(ratiosRes.data);
        setCarTypeMonthlyStats(carTypeRes.data);
        setEvCarMonthlyStats(evCarRes.data);
        setIsMonthLoading(false);
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch monthly stats", error);
        toast.error("データの取得に失敗しました。年月を元に戻します。");
        setSelectedYyyymm(prevSuccessYyyymm.current);
        fetchYyyymm.current = prevSuccessYyyymm.current;
        setIsMonthLoading(false);
      }
    };

    if (isMonthChangeFetch) {
      // 月変更起点のフェッチ: onChangeYyyymm 内のデバウンスで既に待機済みのため即座に実行。
      doFetch();
    } else {
      // 初回表示・営業所切替起点のフェッチ: stats 取得完了後に短い遅延を挟んで実行する。
      delayTimer = setTimeout(() => {
        if (isActive) doFetch();
      }, MONTHLY_STATS_INITIAL_FETCH_DELAY_MS);
    }

    return () => {
      isActive = false;
      if (delayTimer) clearTimeout(delayTimer);
    };
    // fetchYyyymm.current: effect で実際に使う年月。
    //   selectedYyyymm とは意図的に分離しており、月変更デバウンス完了後にのみ更新される。
    //   revert 時は selectedYyyymm / fetchYyyymm を元に戻すが、fetchRevision を更新しないため再フェッチは起きない。
    // fetchRevision: 月変更デバウンス完了時にインクリメントされ、effect の再実行トリガーになる。
    // stats: 初回・営業所切替時のフェッチ開始タイミングを stats 取得完了に合わせるために監視。
    // selectedYyyymm は意図的に除外 — revert 時に再フェッチが起きないようにするため。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOffice, fetchRevision, stats]);

  const chart1Data = useMemo<ComparisonDataItem[]>(
    () => processComparisonData(stats?.operationImprovementAverages.ownOfficeMonthlyAverages || []),
    [stats],
  );

  const chart2Data = useMemo<ComparisonDataItem[]>(
    () => processComparisonData(stats?.operationImprovementAverages.successfulOtherOfficeMonthlyAverages || []),
    [stats],
  );

  const { vehicleChartData, sortedVehicleIds, vehicleLabelById } = useMemo<{
    vehicleChartData: VehicleChartDataItem[];
    sortedVehicleIds: string[];
    vehicleLabelById: Record<string, string>;
  }>(() => {
    if (!stats?.ownOfficeMonthlyCarStats) {
      return { vehicleChartData: [], sortedVehicleIds: [], vehicleLabelById: {} as Record<string, string> };
    }

    const grouped = new Map<string, VehicleChartDataItem>();
    const vehicleMeta = new Map<string, { vehicleCode: string; mileageSum: number; mileageCount: number }>();

    stats.ownOfficeMonthlyCarStats.forEach((stat) => {
      if (!stat.yyyymm) return;
      if (!grouped.has(stat.yyyymm)) {
        grouped.set(stat.yyyymm, { month: stat.yyyymm });
      }
      const row = grouped.get(stat.yyyymm)!;
      if (stat.carId != null) {
        const idStr = String(stat.carId);

        const vehicleCode = stat.vehicleCode?.trim() ? stat.vehicleCode : "車番不明";
        const existingMeta = vehicleMeta.get(idStr);
        if (!existingMeta) {
          vehicleMeta.set(idStr, {
            vehicleCode,
            mileageSum: stat.mileageKm ?? 0,
            mileageCount: stat.mileageKm != null ? 1 : 0,
          });
        } else {
          existingMeta.mileageSum += stat.mileageKm ?? 0;
          if (stat.mileageKm != null) {
            existingMeta.mileageCount += 1;
          }
          if (existingMeta.vehicleCode === "車番不明" && vehicleCode !== "車番不明") {
            existingMeta.vehicleCode = vehicleCode;
          }
        }

        const mileageKey = `v_${idStr}_mileage` as const;
        const revenueKey = `v_${idStr}_revenue` as const;
        row[mileageKey] = stat.mileageKm;
        row[revenueKey] = stat.revenue;
      }
    });

    (stats.operationImprovementAverages?.ownOfficeMonthlyAverages || []).forEach((item) => {
      if (item.isEv || !item.yyyymm) return;
      if (!grouped.has(item.yyyymm)) {
        grouped.set(item.yyyymm, { month: item.yyyymm });
      }
      const row = grouped.get(item.yyyymm)!;
      row.officeLpgMileage = item.avgMileageKm;
      row.officeLpgRevenue = item.avgRevenue;
    });

    const sortedIds = Array.from(vehicleMeta.entries())
      .sort((a, b) => {
        const aAvg = a[1].mileageCount > 0 ? a[1].mileageSum / a[1].mileageCount : Number.POSITIVE_INFINITY;
        const bAvg = b[1].mileageCount > 0 ? b[1].mileageSum / b[1].mileageCount : Number.POSITIVE_INFINITY;
        if (aAvg !== bAvg) return aAvg - bAvg;
        return Number(a[0]) - Number(b[0]);
      })
      .map(([id]) => id);

    const labels = Array.from(vehicleMeta.entries()).reduce<Record<string, string>>((acc, [id, meta]) => {
      acc[id] = `${meta.vehicleCode}`;
      return acc;
    }, {});

    return {
      vehicleChartData: Array.from(grouped.values()).sort((a, b) => a.month.localeCompare(b.month)),
      sortedVehicleIds: sortedIds,
      vehicleLabelById: labels,
    };
  }, [stats]);

  const {
    lowOperationEv,
    bestHighOperation,
    mileageIncreaseValue,
    revenueIncreaseValue,
    grossProfitIncreaseValue,
    monthlyRevenueImprovementText,
  } = useMemo(() => {
    const simulation = stats?.operationImprovementSimulation;
    const low = simulation?.lowOperationEv ?? null;
    const best = simulation?.avgOperationNonEv ?? null;

    const mileageDiff = (best?.mileageKm ?? 0) - (low?.mileageKm ?? 0);
    const mileageIncrease = Math.round(Math.max(mileageDiff, 0));

    const revenueDiff = (best?.revenue ?? 0) - (low?.revenue ?? 0);
    const revenueIncreaseRounded = Math.round(Math.max(revenueDiff, 0));
    const revenueIncrease = revenueIncreaseRounded > 0 ? revenueIncreaseRounded : null;

    const grossProfitDiff = (best?.grossProfit ?? 0) - (low?.grossProfit ?? 0);
    const grossProfitIncreaseRounded = Math.round(Math.max(grossProfitDiff, 0));
    const grossProfitIncrease = grossProfitIncreaseRounded > 0 ? grossProfitIncreaseRounded : null;

    return {
      lowOperationEv: low,
      bestHighOperation: best,
      mileageIncreaseValue: mileageIncrease,
      revenueIncreaseValue: revenueIncrease,
      grossProfitIncreaseValue: grossProfitIncrease,
      monthlyRevenueImprovementText: grossProfitIncreaseRounded.toLocaleString("ja-JP"),
    };
  }, [stats]);

  const chargingFeeStepText = useMemo(() => {
    const stepPrices = stats?.operationImprovementSimulation?.chargingFeeStepPrices || [];

    const formatThreshold = (value: number | null) => {
      if (value == null || Number.isNaN(value)) return null;
      return Math.round(value).toLocaleString("ja-JP");
    };

    const stepTexts = [...stepPrices]
      .sort((a, b) => {
        const aMin = a.minKwhPerMonth ?? Number.POSITIVE_INFINITY;
        const bMin = b.minKwhPerMonth ?? Number.POSITIVE_INFINITY;
        return aMin - bMin;
      })
      .filter((step) => step.unitPriceYenPerKwh != null)
      .map((step) => {
        const minKwh = formatThreshold(step.minKwhPerMonth);
        const maxKwh = formatThreshold(step.maxKwhPerMonth);
        const unitPrice = (step.unitPriceYenPerKwh as number).toLocaleString("ja-JP", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });

        if (minKwh != null && maxKwh != null) {
          return `${minKwh}kWh以上${maxKwh}kWh未満は${unitPrice}円/kWh(税抜)`;
        }
        if (minKwh != null) {
          return `${minKwh}kWh以上は${unitPrice}円/kWh(税抜)`;
        }
        if (maxKwh != null) {
          return `${maxKwh}kWh未満は${unitPrice}円/kWh(税抜)`;
        }
        return `${unitPrice}円/kWh(税抜)`;
      });

    if (!stepTexts.length) {
      return "営業所では階段料金で計算しています";
    }

    return `営業所では${stepTexts.join("、")}で計算しています`;
  }, [stats]);

  const streetViewEmbedUrl = stats?.recommendedCharger?.streetViewUrl
    ? convertStreetViewUrl(stats.recommendedCharger.streetViewUrl)
    : "";

  const googleMapUrl =
    stats?.recommendedCharger?.latitude != null && stats?.recommendedCharger?.longitude != null
      ? buildGoogleMapUrl(stats.recommendedCharger.latitude, stats.recommendedCharger.longitude)
      : "";

  const goChargeUrl =
    stats?.recommendedCharger?.officeLatitude != null && stats?.recommendedCharger?.officeLongitude != null
      ? buildGoChargeUrl(stats.recommendedCharger.officeLatitude, stats.recommendedCharger.officeLongitude)
      : "";

  const motivationVoiceItems: VoiceItem[] = [
    {
      id: "voice-1",
      text: (
        <>
          街中で充電するおかげで、長く走れるようになった。
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>
            24時間営業しているしたくさんある
          </Box>
          から、LPGより夜間も走れる。
        </>
      ),
    },
    {
      id: "voice-2",
      text: (
        <>
          路上駐車ではなく
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>
            充電器スポットでの駐車（充電）になる
          </Box>
          ので、気兼ねなく休憩できる。
        </>
      ),
    },
    {
      id: "voice-3",
      text: (
        <>
          営業所に帰る往復が時間ロスだったのが、街中で充電できるので、
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>
            帰庫時間が短縮されて長く走れる
          </Box>
          ようになった。
        </>
      ),
    },
    {
      id: "voice-4",
      text: (
        <>
          朝 充電器を動かすのが大変だったのが、営業所の充電器を上手く使い回すことで、
          <Box component="span" sx={{ color: TEXT_COLOR.SUCCESS }}>
            シフト組みも楽
          </Box>
          になった。
        </>
      ),
    },
  ];

  const hasNoOffices = offices.length === 0;
  const previousMonthKey = useMemo(() => {
    const currentJstDate = getJSTCurrentTime("date");
    const [year, month] = currentJstDate.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() - 1);
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const hasNoDisplayableOtherOfficeAverages = useMemo(() => {
    const otherOfficeAverages = stats?.operationImprovementAverages?.successfulOtherOfficeMonthlyAverages ?? [];
    if (otherOfficeAverages.length === 0) return true;

    const evMileage = otherOfficeAverages.find(
      (item) => toYYYYMM(item.yyyymm) === previousMonthKey && item.isEv === true,
    )?.avgMileageKm;
    const lpgMileage = otherOfficeAverages.find(
      (item) => toYYYYMM(item.yyyymm) === previousMonthKey && item.isEv === false,
    )?.avgMileageKm;

    return evMileage == null || evMileage <= 0 || lpgMileage == null || lpgMileage <= 0;
  }, [previousMonthKey, stats]);

  const hasInvalidPreviousMonthMileage = useMemo(() => {
    if (!selectedOffice) return false;

    const ownOfficeAverages = stats?.operationImprovementAverages?.ownOfficeMonthlyAverages ?? [];

    const evMileage = ownOfficeAverages.find(
      (item) => toYYYYMM(item.yyyymm) === previousMonthKey && item.isEv === true,
    )?.avgMileageKm;
    const lpgMileage = ownOfficeAverages.find(
      (item) => toYYYYMM(item.yyyymm) === previousMonthKey && item.isEv === false,
    )?.avgMileageKm;

    return evMileage == null || evMileage <= 0 || lpgMileage == null || lpgMileage <= 0;
  }, [previousMonthKey, selectedOffice, stats]);

  const shouldShowPreparingLayout =
    hasOperationImprovementStatsConflict ||
    hasNoOffices ||
    hasNoDisplayableOtherOfficeAverages ||
    hasInvalidPreviousMonthMileage;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (shouldShowPreparingLayout) {
    return <PreparingLayout offices={offices} selectedOffice={selectedOffice} onChangeOffice={onChangeOffice} />;
  }

  return (
    <Box
      sx={[
        GO_FURTHER_PAGE_SX,
        { bgcolor: BACKGROUND_COLOR.INHERIT, color: TEXT_COLOR.PRIMARY, minHeight: "100vh", minWidth: 960 },
      ]}
    >
      <Paper sx={{ width: "960px", mx: "auto", bgcolor: "inherit", border: "none", boxShadow: "none", boxSizing: "border-box" }}>
        <GoFurtherHeroSection offices={offices} selectedOffice={selectedOffice} onChangeOffice={onChangeOffice} />

        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            mt: 5,
            "& > *": {
              mt: 4,
            },
            px: 0,
          }}
        >
          <GoFurtherOfficeSimulationSection
            selectedOffice={selectedOffice}
            chartData={chart1Data}
            baseNonEvRevenue={baseNonEvRevenue}
            onApplyBaseNonEvRevenue={onApplyBaseNonEvRevenue}
          />

          <GoFurtherSuccessCaseSection chartData={chart2Data} />

          <GoFurtherPhaseSection
            vehicleChartData={vehicleChartData}
            sortedVehicleIds={sortedVehicleIds}
            vehicleLabelById={vehicleLabelById}
            lowOperationEv={lowOperationEv}
            bestHighOperation={bestHighOperation}
            mileageIncreaseValue={mileageIncreaseValue}
            revenueIncreaseValue={revenueIncreaseValue}
            grossProfitIncreaseValue={grossProfitIncreaseValue}
            monthlyRevenueImprovementText={monthlyRevenueImprovementText}
            chargingFeeStepText={chargingFeeStepText}
            personaType={selectedOffice?.personaType}
            officeName={selectedOffice?.name ?? ""}
            evExternalChargingRatios={evExternalChargingRatios}
            carTypeMonthlyStats={carTypeMonthlyStats}
            evCarMonthlyStats={evCarMonthlyStats}
            selectedYyyymm={selectedYyyymm}
            isMonthLoading={isMonthLoading}
            onChangeYyyymm={(yyyymm) => {
              setSelectedYyyymm(yyyymm);
              setIsMonthLoading(true);
              if (monthChangeDebounceRef.current) clearTimeout(monthChangeDebounceRef.current);
              monthChangeDebounceRef.current = setTimeout(() => {
                fetchYyyymm.current = yyyymm;
                isMonthChangeFetchRef.current = true;
                setFetchRevision((r) => r + 1);
              }, MONTH_CHANGE_DEBOUNCE_MS);
            }}
          />

          <GoFurtherActionSection
            recommendedCharger={stats?.recommendedCharger}
            streetViewEmbedUrl={streetViewEmbedUrl}
            googleMapUrl={googleMapUrl}
            goChargeUrl={goChargeUrl}
            selectedOffice={selectedOffice}
            personaType={selectedOffice?.personaType}
            driverGuideId={stats?.driverGuideId}
            voiceItems={motivationVoiceItems}
          />

          <GoFurtherClosingSection isPersonaAB={isPersonaAB} />
        </Container>
      </Paper>
    </Box>
  );
};

export default GoFurther;
